'use client';

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface FlashNotification {
  id: string;
  trigger_type: 'attendance' | 'fee_payment' | 'enrollment' | 'whatsapp_delivery' | string;
  title: string;
  message: string;
  audience: string;
  target_user_id?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
}

interface NotificationContextValue {
  flashNotifs: FlashNotification[];
  unreadCount: number;
  isFlashing: boolean;
  dismissFlash: (id: string) => void;
  dismissAll: () => void;
  broadcastNotification: (notif: Omit<FlashNotification, 'id' | 'created_at'>) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue>({
  flashNotifs: [],
  unreadCount: 0,
  isFlashing: false,
  dismissFlash: () => {},
  dismissAll: () => {},
  broadcastNotification: async () => {},
});

export function useNotifications() {
  return useContext(NotificationContext);
}

// Beep sound generator using Web Audio API
function playBeep(frequency = 880, duration = 200, volume = 0.4) {
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration / 1000);
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration / 1000);
  } catch {}
}

function playAlertBeep() {
  // Double beep pattern for alerts
  playBeep(880, 150, 0.5);
  setTimeout(() => playBeep(1100, 200, 0.6), 200);
}

const TRIGGER_CONFIG: Record<string, { color: string; emoji: string }> = {
  attendance: { color: '#10B981', emoji: '✅' },
  fee_payment: { color: '#F59E0B', emoji: '💰' },
  enrollment: { color: '#2563EB', emoji: '🎓' },
  whatsapp_delivery: { color: '#25D366', emoji: '📱' },
  general: { color: '#1E3A5F', emoji: '🔔' },
};

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [flashNotifs, setFlashNotifs] = useState<FlashNotification[]>([]);
  const [isFlashing, setIsFlashing] = useState(false);
  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user } = useAuth();
  const supabase = createClient();

  const triggerFlash = useCallback(() => {
    setIsFlashing(true);
    playAlertBeep();
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    flashTimerRef.current = setTimeout(() => setIsFlashing(false), 3000);
  }, []);

  const dismissFlash = useCallback((id: string) => {
    setFlashNotifs(prev => prev.filter(n => n.id !== id));
  }, []);

  const dismissAll = useCallback(() => {
    setFlashNotifs([]);
    setIsFlashing(false);
  }, []);

  const broadcastNotification = useCallback(async (notif: Omit<FlashNotification, 'id' | 'created_at'>) => {
    try {
      await supabase.from('flash_notifications').insert({
        trigger_type: notif.trigger_type,
        title: notif.title,
        message: notif.message,
        audience: notif.audience,
        target_user_id: notif.target_user_id || null,
        metadata: notif.metadata || {},
      });
    } catch {}
  }, [supabase]);

  useEffect(() => {
    // Subscribe to real-time flash notifications
    const channel = supabase
      .channel('flash_notifications_realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'flash_notifications' },
        (payload) => {
          const notif = payload.new as FlashNotification;

          // Filter: show if audience matches user role or is 'all'
          const shouldShow =
            notif.audience === 'all' ||
            notif.audience === 'admin' ||
            notif.audience === 'staff' ||
            notif.audience === 'student' ||
            (user && notif.target_user_id === user.id);

          if (!shouldShow) return;

          setFlashNotifs(prev => [notif, ...prev.slice(0, 9)]); // keep max 10
          triggerFlash();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, [user, supabase, triggerFlash]);

  const unreadCount = flashNotifs.length;

  return (
    <NotificationContext.Provider value={{ flashNotifs, unreadCount, isFlashing, dismissFlash, dismissAll, broadcastNotification }}>
      {children}
      <FlashAlertOverlay
        notifs={flashNotifs}
        isFlashing={isFlashing}
        onDismiss={dismissFlash}
        onDismissAll={dismissAll}
      />
    </NotificationContext.Provider>
  );
}

// ─── Flash Alert Overlay ─────────────────────────────────────────────────────

interface FlashAlertOverlayProps {
  notifs: FlashNotification[];
  isFlashing: boolean;
  onDismiss: (id: string) => void;
  onDismissAll: () => void;
}

function FlashAlertOverlay({ notifs, isFlashing, onDismiss, onDismissAll }: FlashAlertOverlayProps) {
  if (notifs.length === 0) return null;

  const latest = notifs[0];
  const cfg = TRIGGER_CONFIG[latest.trigger_type] || TRIGGER_CONFIG.general;

  return (
    <>
      {/* Screen flash border effect */}
      {isFlashing && (
        <div
          className="fixed inset-0 pointer-events-none z-[9998]"
          style={{
            boxShadow: `inset 0 0 0 4px ${cfg.color}`,
            animation: 'flashBorder 0.5s ease-in-out 3',
          }}
        />
      )}

      {/* Notification stack */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 max-w-sm w-full">
        {notifs.slice(0, 3).map((notif, idx) => {
          const c = TRIGGER_CONFIG[notif.trigger_type] || TRIGGER_CONFIG.general;
          return (
            <div
              key={notif.id}
              className="rounded-2xl shadow-2xl overflow-hidden"
              style={{
                background: '#0F172A',
                border: `2px solid ${c.color}`,
                transform: `scale(${1 - idx * 0.03}) translateY(${idx * 4}px)`,
                opacity: 1 - idx * 0.15,
                animation: idx === 0 && isFlashing ? 'flashCard 0.4s ease-in-out 3' : undefined,
                boxShadow: `0 0 24px ${c.color}40, 0 8px 32px rgba(0,0,0,0.5)`,
              }}
            >
              {/* Colored top bar */}
              <div className="h-1 w-full" style={{ background: c.color }} />

              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {/* Icon */}
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
                      style={{ background: `${c.color}20`, border: `1px solid ${c.color}40` }}
                    >
                      {c.emoji}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                          style={{ background: `${c.color}20`, color: c.color }}
                        >
                          {notif.trigger_type.replace('_', ' ')}
                        </span>
                        {isFlashing && idx === 0 && (
                          <span
                            className="text-xs font-bold px-2 py-0.5 rounded-full animate-pulse"
                            style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }}
                          >
                            LIVE
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-bold text-white leading-snug">{notif.title}</p>
                      <p className="text-xs text-slate-400 mt-0.5 leading-relaxed line-clamp-2">{notif.message}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => onDismiss(notif.id)}
                    className="text-slate-500 hover:text-white transition-colors flex-shrink-0 mt-0.5"
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                      <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>

                {notifs.length > 1 && idx === 0 && (
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-slate-500">+{notifs.length - 1} more alert{notifs.length > 2 ? 's' : ''}</span>
                    <button
                      onClick={onDismissAll}
                      className="text-xs font-semibold transition-colors"
                      style={{ color: c.color }}
                    >
                      Dismiss all
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes flashBorder {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
        @keyframes flashCard {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
      `}</style>
    </>
  );
}
